/**
 * Navigation scope of the landlord app, three strict levels (docs/09-screens.md "Navigation"):
 * 1. Konto ("Alle Objekte"): account-wide lists (/, /belege, /zahlungen …),
 * 2. Objekt: /objekte/:id/<section>, every section is a view over the whole house,
 * 3. Einheit: /einheiten/:id/<tab>, every tab and action stays scoped to that unit; a tenancy opens inside its unit
 *    (/einheiten/:unitId/mietverhaeltnisse/:id/<tab>). Old /mietverhaeltnisse/:id links redirect there.
 * The URL is the source of truth (reload and browser back work). Pure helpers here (tested in test/scope.test.ts);
 * the hook lives in shell/useScope.ts.
 */

/** Sections every property has (in sidebar order); `hausgeld` only for WEG units. */
export const PROPERTY_SECTIONS = [
  'uebersicht',
  'einheiten',
  'vertraege',
  'belege',
  'zaehler',
  'zahlungen',
  'abrechnungen',
  'umlage',
  'chronik',
  'hausgeld',
  'einstellungen',
] as const;
export type PropertySection = (typeof PROPERTY_SECTIONS)[number];

/** Sub pages of a property and the section they belong to (sidebar highlight, switching property keeps the section). */
const SUB_PAGES: Record<string, PropertySection> = {
  einheiten: 'einheiten',
  import: 'einheiten',
  vertraege: 'vertraege',
  belege: 'belege',
  kosten: 'belege',
  zaehler: 'zaehler',
  zahlungen: 'zahlungen',
  abrechnungen: 'abrechnungen',
  zeitraeume: 'abrechnungen',
  umlage: 'umlage',
  chronik: 'chronik',
  hausgeld: 'hausgeld',
  akte: 'uebersicht',
  einstellungen: 'einstellungen',
};

/** Account-wide pages and the property section with the same content. */
const GLOBAL_TO_SECTION: Record<string, PropertySection> = {
  '': 'uebersicht',
  objekte: 'uebersicht',
  personen: 'vertraege',
  belege: 'belege',
  zaehler: 'zaehler',
  zahlungen: 'zahlungen',
  abrechnungen: 'abrechnungen',
};
/** Property section → account-wide page ("Alle Objekte"); sections without an account-wide page go to the overview. */
const SECTION_TO_GLOBAL: Record<PropertySection, string> = {
  uebersicht: '/',
  einheiten: '/objekte',
  vertraege: '/personen',
  belege: '/belege',
  zaehler: '/zaehler',
  zahlungen: '/zahlungen',
  abrechnungen: '/abrechnungen',
  umlage: '/',
  chronik: '/',
  hausgeld: '/',
  einstellungen: '/',
};

/** Tabs of a unit (level 3), in tab order; `uebersicht` is the unit's root path. */
export const UNIT_TABS = [
  'uebersicht',
  'mietverhaeltnisse',
  'zaehler',
  'konto',
  'abrechnungen',
  'dokumente',
  'zeitachse',
  'chronik',
] as const;
export type UnitTab = (typeof UNIT_TABS)[number];
/** Tabs of a tenancy inside its unit; `anpassen` (prepayment dialog over the account) belongs to `konto`. */
export const TENANCY_TABS = [
  'uebersicht',
  'konto',
  'kaution',
  'dokumente',
  'mahnung',
  'nachrichten',
  'einstellungen',
  'chronik',
] as const;
export type TenancyTab = (typeof TENANCY_TABS)[number];

/**
 * Tab bar of a unit (level 3): Übersicht · Mietverhältnis · Zähler · Konto · Dokumente · Verlauf. Abrechnungen,
 * Zeitachse and Chronik keep their own routes and are the views inside "Verlauf" (sub navigation under the tabs).
 */
export const UNIT_TAB_BAR = ['uebersicht', 'mietverhaeltnisse', 'zaehler', 'konto', 'dokumente', 'verlauf'] as const;
export type UnitTabBar = (typeof UNIT_TAB_BAR)[number];
/** Views of the "Verlauf" tab, in sub navigation order; the first one is where the tab opens. */
export const UNIT_HISTORY_VIEWS = ['abrechnungen', 'zeitachse', 'chronik'] as const satisfies readonly UnitTab[];
/** Tab of the tab bar a unit route belongs to. */
export const unitTabBarOf = (tab: UnitTab): UnitTabBar =>
  (UNIT_HISTORY_VIEWS as readonly string[]).includes(tab) ? 'verlauf' : (tab as UnitTabBar);

/**
 * Tab bar of a tenancy: Übersicht · Konto & Mahnung · Kaution · Nachrichten · Einstellungen. Dokumente (inside
 * Übersicht), Mahnung (inside Konto) and Chronik (inside Einstellungen) keep their own routes as views of a tab.
 */
export const TENANCY_TAB_BAR = ['uebersicht', 'konto', 'kaution', 'nachrichten', 'einstellungen'] as const;
export type TenancyTabBar = (typeof TENANCY_TAB_BAR)[number];
const TENANCY_TAB_GROUP: Record<TenancyTab, TenancyTabBar> = {
  uebersicht: 'uebersicht',
  dokumente: 'uebersicht',
  konto: 'konto',
  mahnung: 'konto',
  kaution: 'kaution',
  nachrichten: 'nachrichten',
  einstellungen: 'einstellungen',
  chronik: 'einstellungen',
};
/** Views inside one tenancy tab (sub navigation); tabs without an entry have a single view. */
export const TENANCY_TAB_VIEWS: Partial<Record<TenancyTabBar, TenancyTab[]>> = {
  uebersicht: ['uebersicht', 'dokumente'],
  konto: ['konto', 'mahnung'],
  einstellungen: ['einstellungen', 'chronik'],
};
export const tenancyTabBarOf = (tab: TenancyTab): TenancyTabBar => TENANCY_TAB_GROUP[tab];

/** Old unit tab names (?tab=…, /einheiten/:id/vertraege) → current tab; "verlauf" opens its first view. */
export function unitTabOf(raw: string | null | undefined): UnitTab {
  if (raw === 'vertraege') return 'mietverhaeltnisse';
  if (raw === 'verlauf') return UNIT_HISTORY_VIEWS[0];
  return (UNIT_TABS as readonly string[]).includes(raw ?? '') ? (raw as UnitTab) : 'uebersicht';
}
export function tenancyTabOf(raw: string | null | undefined): TenancyTab {
  if (raw === 'anpassen') return 'konto';
  return (TENANCY_TABS as readonly string[]).includes(raw ?? '') ? (raw as TenancyTab) : 'uebersicht';
}

/** Path of a unit tab (level 3). */
export const unitPath = (unitId: string, tab: UnitTab = 'uebersicht') =>
  tab === 'uebersicht' ? `/einheiten/${unitId}` : `/einheiten/${unitId}/${tab}`;

/**
 * Path of a tenancy page inside its unit. Without the unit id (rows that only know the tenancy) the old
 * /mietverhaeltnisse/:id route is used; it redirects to the nested route once the tenancy is loaded.
 * `sub` may also be `anpassen` (prepayment dialog over the account).
 */
export function tenancyPath(
  x: { id: string; unitId?: string | null },
  sub: TenancyTab | 'anpassen' = 'uebersicht',
): string {
  const tail = sub === 'uebersicht' ? '' : `/${sub}`;
  return x.unitId
    ? `/einheiten/${x.unitId}/mietverhaeltnisse/${x.id}${tail}`
    : `/mietverhaeltnisse/${x.id}${tail}`;
}

/**
 * Path of an Übergabeprotokoll (editor or read-only view) inside its tenancy; without the unit id the old
 * /mietverhaeltnisse/:id/uebergabe/:handoverId route redirects once the tenancy is loaded (deadlines know no unit).
 */
export const handoverPath = (x: { id: string; unitId?: string | null }, handoverId: string) =>
  x.unitId
    ? `/einheiten/${x.unitId}/mietverhaeltnisse/${x.id}/uebergabe/${handoverId}`
    : `/mietverhaeltnisse/${x.id}/uebergabe/${handoverId}`;

/**
 * Target of an old tenancy link (/mietverhaeltnisse/:id[/konto|kaution|mahnung|anpassen][?tab=…]) once the tenancy's
 * unit is known: the nested route, `?tab=` becomes the tab path, every other query parameter (aktion, stufe) stays.
 */
export function legacyTenancyTarget(
  unitId: string,
  id: string,
  sub: string | undefined,
  search: string,
): string {
  const q = new URLSearchParams(search);
  const tab = q.get('tab');
  q.delete('tab');
  const target: TenancyTab | 'anpassen' =
    sub === 'anpassen' ? 'anpassen' : sub ? tenancyTabOf(sub) : tenancyTabOf(tab);
  const rest = q.toString();
  return `${tenancyPath({ id, unitId }, target)}${rest ? `?${rest}` : ''}`;
}

/**
 * What a path says about the scope:
 * - `property`: a page of one property (`section` for the sidebar),
 * - `unit`: level 3, a tab of one unit or a tenancy inside it; its property comes from the loaded unit,
 * - `entity`: an old tenancy link (redirects into its unit) or a billing period page; property from the loaded entity,
 * - `all`: an account-wide list ("Alle Objekte"),
 * - `neutral`: account pages without property context (Mitteilungen, Fristen, Einstellungen …): the last property stays.
 */
export type PathScope =
  | { kind: 'property'; propertyId: string; section: PropertySection }
  | {
      kind: 'unit';
      unitId: string;
      tab: UnitTab;
      tenancyId?: string;
      tenancyTab?: TenancyTab;
      section: 'einheiten';
    }
  | { kind: 'entity'; entity: 'tenancy' | 'period'; id: string; section: PropertySection }
  | { kind: 'all'; section: PropertySection }
  | { kind: 'neutral' };

export function scopeOfPath(pathname: string): PathScope {
  const seg = pathname.split('/').filter(Boolean);
  const [first = '', second, third, fourth, fifth] = seg;
  if (first === 'objekte' && second && second !== 'neu')
    return {
      kind: 'property',
      propertyId: second,
      section: third ? (SUB_PAGES[third] ?? 'uebersicht') : 'uebersicht',
    };
  if (first === 'einheiten' && second) {
    if (third === 'mietverhaeltnisse' && fourth)
      return {
        kind: 'unit',
        unitId: second,
        tab: 'mietverhaeltnisse',
        tenancyId: fourth,
        tenancyTab: tenancyTabOf(fifth),
        section: 'einheiten',
      };
    return { kind: 'unit', unitId: second, tab: unitTabOf(third), section: 'einheiten' };
  }
  if (first === 'mietverhaeltnisse' && second)
    return {
      kind: 'entity',
      entity: 'tenancy',
      id: second,
      section: third === 'konto' || third === 'mahnung' ? 'zahlungen' : 'vertraege',
    };
  if (first === 'abrechnungen' && second)
    return { kind: 'entity', entity: 'period', id: second, section: 'abrechnungen' };
  if (seg.length <= 1 && first in GLOBAL_TO_SECTION)
    return { kind: 'all', section: GLOBAL_TO_SECTION[first]! };
  return { kind: 'neutral' };
}

/** Path of a property section. */
export const sectionPath = (propertyId: string, section: PropertySection) =>
  section === 'uebersicht' ? `/objekte/${propertyId}` : `/objekte/${propertyId}/${section}`;

/**
 * Where the switcher goes: the same section of the chosen property, or its account-wide page for "Alle Objekte"
 * (`target` null). Neutral pages open the property overview or the dashboard.
 */
export function switchTarget(pathname: string, target: string | null, isWeg = false): string {
  const s = scopeOfPath(pathname);
  const section: PropertySection = s.kind === 'neutral' ? 'uebersicht' : s.section;
  if (!target) return SECTION_TO_GLOBAL[section];
  return sectionPath(target, section === 'hausgeld' && !isWeg ? 'uebersicht' : section);
}

/**
 * Navigation level of a page (design/NavKonzept): `einheit` for a unit and the tenancies inside it (level 3),
 * `objekt` for the pages of one property (its sections, billing periods), `portfolio` ("Alle Objekte") for the
 * account-wide lists and the account pages (Mitteilungen, Fristen, Einstellungen), which are about every property.
 */
export type NavLevel = 'portfolio' | 'objekt' | 'einheit';
export function navLevel(path: PathScope, propertyId: string | null): NavLevel {
  if (!propertyId) return 'portfolio';
  if (path.kind === 'unit') return 'einheit';
  return path.kind === 'property' || path.kind === 'entity' ? 'objekt' : 'portfolio';
}

export type TrailCrumb = { to: string; label: string };
/** Header trail: crumbs, whether the page is the last crumb itself, and the logical parent for "← Zurück zu …". */
export type Trail = { crumbs: TrailCrumb[]; atRoot: boolean; back: TrailCrumb | null };

/**
 * Logical parent of a page (not the browser history): one step up the breadcrumb. On the crumb's own page that is
 * the crumb before it, below the last crumb (a sub page that adds its own title) the last crumb.
 */
export function backOf(t: { crumbs: TrailCrumb[]; atRoot: boolean }): TrailCrumb | null {
  const i = t.atRoot ? t.crumbs.length - 2 : t.crumbs.length - 1;
  return i >= 0 ? t.crumbs[i]! : null;
}
/**
 * Breadcrumb trail of a property page: "Alle Objekte › <Objekt> › <Bereich>". On the section page itself
 * (`atRoot`) its last entry is the current page; below a section (a unit, a tenancy, a sub page) every entry is a link
 * and the page adds its own title. The overview has no section entry ("Alle Objekte › <Objekt>").
 */
export function propertyTrail(
  pathname: string,
  propertyId: string,
  section: PropertySection,
  labels: { all: string; property: string; section: string },
): Trail {
  const crumbs: TrailCrumb[] = [
    { to: '/', label: labels.all },
    { to: sectionPath(propertyId, 'uebersicht'), label: labels.property },
  ];
  if (section !== 'uebersicht') crumbs.push({ to: sectionPath(propertyId, section), label: labels.section });
  const here = pathname.replace(/\/+$/, '') || '/';
  const atRoot = here === crumbs[crumbs.length - 1]!.to;
  return { crumbs, atRoot, back: backOf({ crumbs, atRoot }) };
}

/**
 * Trail of a unit page (level 3), every page is its last crumb:
 * "Alle Objekte › <Objekt> › Einheiten › <Einheit> [› <Tab>]" and inside a tenancy
 * "… › <Einheit> › Mietverhältnisse › <Mietverhältnis> [› <Tab>]". `labels.tab` / `labels.tenancyTab` name the open
 * tab (only used when it is not the overview); a tenancy whose name is not loaded yet still gets its crumb.
 */
export function unitTrail(
  path: Extract<PathScope, { kind: 'unit' }>,
  propertyId: string,
  labels: {
    all: string;
    property: string;
    units: string;
    unit: string;
    tab: string;
    tenancy?: string;
    tenancyTab?: string;
  },
): Trail {
  const crumbs: TrailCrumb[] = [
    { to: '/', label: labels.all },
    { to: sectionPath(propertyId, 'uebersicht'), label: labels.property },
    { to: sectionPath(propertyId, 'einheiten'), label: labels.units },
    { to: unitPath(path.unitId), label: labels.unit },
  ];
  if (path.tab !== 'uebersicht') crumbs.push({ to: unitPath(path.unitId, path.tab), label: labels.tab });
  if (path.tenancyId) {
    const x = { id: path.tenancyId, unitId: path.unitId };
    crumbs.push({ to: tenancyPath(x), label: labels.tenancy ?? '' });
    if (path.tenancyTab && path.tenancyTab !== 'uebersicht')
      crumbs.push({ to: tenancyPath(x, path.tenancyTab), label: labels.tenancyTab ?? '' });
  }
  return { crumbs, atRoot: true, back: backOf({ crumbs, atRoot: true }) };
}
